import React, { useState, useEffect } from 'react'
import { alpha, useTheme } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import {
  SEVV_DIALOG_PAPER_BACKGROUND_SOLID,
  SEVV_DIALOG_PAPER_BORDER_DIVIDER,
  SEVV_DIALOG_PAPER_SHADOW,
  sevvDialogTitleChromeSx,
} from '../theme/sevvDialogChrome'
import {
  jobsService,
  Job,
  JobExecution,
  JobOverview,
  ADMIN_JOB_TYPES,
  type AdminJobType,
} from '../services/JobsService'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material'
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Stop as StopIcon,
  Close as CloseIcon,
} from '@mui/icons-material'

const SERVER_JOBS_DIALOG_PAPER_SX: SxProps<Theme> = {
  p: 0,
  overflow: 'hidden',
  borderRadius: '8px',
  backgroundColor: SEVV_DIALOG_PAPER_BACKGROUND_SOLID,
  backgroundImage: 'none',
  border: SEVV_DIALOG_PAPER_BORDER_DIVIDER,
  boxShadow: SEVV_DIALOG_PAPER_SHADOW,
}

const SERVER_JOBS_DIALOG_TITLE_SX: SxProps<Theme> = {
  ...sevvDialogTitleChromeSx,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  flexShrink: 0,
  padding: '18px 24px 16px',
  fontWeight: 600,
}

const SERVER_JOBS_DIALOG_ACTIONS_SX: SxProps<Theme> = {
  px: 3,
  py: 2,
  gap: '8px',
  flexShrink: 0,
  borderTop: '1px solid var(--sevv-divider)',
  backgroundColor: 'var(--sevv-dialog-actions-bg)',
}

type StatusPaletteKey = 'primary' | 'success' | 'error' | 'warning' | 'text'

const STATUS_META: Record<
  string,
  { palette: StatusPaletteKey; icon: string; label: string }
> = {
  running: { palette: 'primary', icon: '🔄', label: 'Running' },
  completed: { palette: 'success', icon: '✅', label: 'Completed' },
  failed: { palette: 'error', icon: '❌', label: 'Failed' },
  idle: { palette: 'text', icon: '⏸️', label: 'Idle' },
  paused: { palette: 'warning', icon: '⏸️', label: 'Paused' },
  pending: { palette: 'warning', icon: '⏳', label: 'Pending' },
  queued: { palette: 'warning', icon: '⏳', label: 'Queued' },
  timeout: { palette: 'warning', icon: '⏰', label: 'Timeout' },
  cancelled: { palette: 'text', icon: '🚫', label: 'Cancelled' },
  disabled: { palette: 'text', icon: '🚫', label: 'Disabled' },
}

function statusMainColor(theme: Theme, key: StatusPaletteKey): string {
  if (key === 'text') return theme.palette.text.secondary
  return theme.palette[key].main
}

/** Job / execution status pill — theme palette + alpha (no hard-coded Tailwind hex). */
const StatusChip: React.FC<{ status?: string; label?: string }> = ({ status, label }) => {
  const theme = useTheme()
  const raw = status && typeof status === 'string' ? status.toLowerCase() : ''
  const meta = STATUS_META[raw] ?? { palette: 'text' as const, icon: '❓', label: 'Unknown' }
  const main = statusMainColor(theme, meta.palette)
  const bg = alpha(main, theme.palette.mode === 'dark' ? 0.2 : 0.12)
  const border = alpha(main, 0.35)
  const displayLabel = label || meta.label

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        backgroundColor: bg,
        border: '1px solid',
        borderColor: border,
        borderRadius: '8px',
        transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          backgroundColor: alpha(main, theme.palette.mode === 'dark' ? 0.28 : 0.18),
          borderColor: alpha(main, 0.5),
          boxShadow: theme.shadows[1],
        },
      }}
    >
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: main,
          textTransform: 'capitalize',
          letterSpacing: '0.02em',
          fontFamily: theme.typography.fontFamily,
          lineHeight: 1.4,
        }}
      >
        {meta.icon} {displayLabel}
      </Typography>
    </Box>
  )
}


interface ServerJobsPanelProps {
  onClose: () => void
}

const JOB_TYPE_LABELS: Record<AdminJobType, string> = {
  health_check: 'Health check (process metrics)',
  system_cleanup: 'System cleanup (trim execution history)',
  stripe_sync: 'Stripe billing batch sync (Parse accounts with Stripe customer; webhooks stay primary)',
  colyseus_room_stats: 'Colyseus room stats (read-only listing)',
  payment_method_expiry_scan: 'Stripe payment method expiry notifications',
  roadmap_vote_digest: 'Roadmap vote digest email (platform admins, transactional template)',
}

const ServerJobsPanel: React.FC<ServerJobsPanelProps> = ({ onClose }) => {
  const [jobs, setJobs] = useState<Job[]>([])
  const [overview, setOverview] = useState<JobOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [executions, setExecutions] = useState<JobExecution[]>([])
  const [showJobDetails, setShowJobDetails] = useState(false)
  const [showCreateJob, setShowCreateJob] = useState(false)
  const [newJob, setNewJob] = useState<{
    name: string
    description: string
    schedule: string
    enabled: boolean
    jobType: AdminJobType
  }>({
    name: '',
    description: '',
    schedule: '1h',
    enabled: true,
    jobType: 'health_check',
  })
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const theme = useTheme()

  // Load jobs data
  const loadJobs = async () => {
    try {
      setLoading(true)
      setError(null)

      const [jobsResult, overviewResult] = await Promise.all([
        jobsService.getJobs(),
        jobsService.getJobOverview()
      ])

      if (jobsResult.success) {
        setJobs(jobsResult.jobs)
      } else {
        console.error('Failed to load jobs:', jobsResult.error)
        setError(jobsResult.error || 'Failed to load jobs data')
      }

      if (overviewResult.success) {
        setOverview(overviewResult.overview)
      } else {
        console.error('Failed to load overview:', overviewResult.error)
        // Don't set error for overview failure, just log it
      }
    } catch (error: any) {
      console.error('Error loading jobs:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  // Load job executions
  const loadJobExecutions = async (jobId: string) => {
    try {
      const result = await jobsService.getJobHistory(jobId, 50)
      if (result.success) {
        setExecutions(result.executions)
      } else {
        console.error('Failed to load job executions:', result.error)
      }
    } catch (error) {
      console.error('Error loading job executions:', error)
    }
  }

  // Toggle job enabled/disabled
  const toggleJob = async (jobId: string, enabled: boolean) => {
    try {
      setActionLoading(jobId)
      const result = await jobsService.toggleJob(jobId, enabled)

      if (result.success) {
        await loadJobs()
      } else {
        setError(result.error || 'Failed to toggle job')
      }
    } catch (error: any) {
      setError(error.message)
    } finally {
      setActionLoading(null)
    }
  }

  // Run job immediately
  const runJobNow = async (jobId: string) => {
    try {
      setActionLoading(jobId)
      const result = await jobsService.runJob(jobId)

      if (result.success) {
        await loadJobs()
      } else {
        setError(result.error || 'Failed to run job')
      }
    } catch (error: any) {
      setError(error.message)
    } finally {
      setActionLoading(null)
    }
  }

  // Create new job
  const createJob = async () => {
    try {
      setActionLoading('create')
      const result = await jobsService.createJob(newJob)

      if (result.success) {
        setShowCreateJob(false)
        setNewJob({
          name: '',
          description: '',
          schedule: '1h',
          enabled: true,
          jobType: 'health_check',
        })
        await loadJobs()
      } else {
        setError(result.error || 'Failed to create job')
      }
    } catch (error: any) {
      setError(error.message)
    } finally {
      setActionLoading(null)
    }
  }

  // Show job details
  const showJobDetailsDialog = async (job: Job) => {
    setSelectedJob(job)
    setShowJobDetails(true)
    await loadJobExecutions(job.id)
  }

  // Format duration
  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`
    return `${(ms / 3600000).toFixed(1)}h`
  }

  // Format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'Never'
    }
    
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) {
        return 'Invalid date'
      }
      return date.toLocaleString()
    } catch (error) {
      console.warn('Error formatting date:', error, 'dateString:', dateString)
      return 'Invalid date'
    }
  }

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return <CircularProgress size={16} />
      case 'completed': return <CheckCircleIcon fontSize="small" />
      case 'failed': return <ErrorIcon fontSize="small" />
      case 'idle': return <ScheduleIcon fontSize="small" />
      case 'disabled':
        return <StopIcon fontSize="small" />
      default: return <InfoIcon fontSize="small" />
    }
  }

  useEffect(() => {
    loadJobs()

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadJobs()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadJobs()
      }
    }, 30000)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  const panelPaperSx: SxProps<Theme> = {
    borderRadius: '8px',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    boxShadow: theme.shadows[theme.palette.mode === 'dark' ? 8 : 4],
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }

  const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    p: 3,
    bgcolor: 'action.hover',
    backgroundImage: 'none',
    borderBottom: '1px solid',
    borderColor: 'divider',
  }

  const contentSx: SxProps<Theme> = {
    p: 3,
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    display: 'flex',
    flexDirection: 'column',
  }

  const tableContainerSx: SxProps<Theme> = {
    mt: 2,
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    borderRadius: '8px',
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    boxShadow: 'none',
    '& .MuiTableCell-root': {
      borderColor: 'divider',
      py: 2,
      px: 2.5,
      fontFamily: theme.typography.fontFamily,
      color: 'text.primary',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      bgcolor: 'action.hover',
      backgroundImage: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: 'text.primary',
    },
    '& .MuiTableBody-root .MuiTableRow-root': {
      transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.shorter }),
      '&:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.06),
      },
    },
  }

  const statCardSx: SxProps<Theme> = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    boxShadow: 'none',
    transition: theme.transitions.create(['border-color', 'box-shadow'], {
      duration: theme.transitions.duration.shorter,
    }),
  }

  const createJobFieldSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& fieldset': { borderColor: 'divider' },
      '&:hover fieldset': { borderColor: 'primary.main' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main' },
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      '&.Mui-focused': { color: 'primary.main' },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
  }

  return (
    <Paper elevation={0} sx={panelPaperSx}>
      <Box sx={headerSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              backgroundImage: 'none',
              color: 'primary.main',
              borderRadius: '8px',
              width: 48,
              height: 48,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.28),
            }}
          >
            <ScheduleIcon sx={{ fontSize: '1.5rem' }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '1.875rem',
                lineHeight: 1.25,
                fontFamily: theme.typography.fontFamily,
                letterSpacing: '-0.025em',
                mb: 0.5,
              }}
            >
              Server Jobs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem', lineHeight: 1.5 }}>
              Manage scheduled tasks and background jobs
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <IconButton aria-label="Close server jobs" onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={loadJobs}
            disabled={loading}
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              fontWeight: 500,
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                backgroundImage: 'none',
              },
            }}
          >
            {loading ? <CircularProgress size={18} color="primary" /> : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SettingsIcon />}
            onClick={() => setShowCreateJob(true)}
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              borderRadius: '8px',
              textTransform: 'none',
            }}
          >
            Create Job
          </Button>
        </Box>
      </Box>

      <Box sx={contentSx}>
        {error ? (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
              bgcolor: 'action.hover',
              backgroundImage: 'none',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Overview Cards */}
            {overview ? (
              <Grid container spacing={2} sx={{ mb: 3, flexShrink: 0 }}>
                {(
                  [
                    { value: overview.totalJobs, label: 'Total Jobs', key: 'primary' as const },
                    { value: overview.enabledJobs, label: 'Enabled', key: 'success' as const },
                    { value: overview.runningJobs, label: 'Running', key: 'info' as const },
                    { value: overview.failedJobs, label: 'Failed', key: 'error' as const },
                  ] as const
                ).map(({ value, label, key: paletteKey }) => {
                  const accent = theme.palette[paletteKey].main
                  return (
                    <Grid item xs={12} sm={6} md={3} key={label}>
                      <Card
                        sx={{
                          ...statCardSx,
                          '&:hover': {
                            borderColor: alpha(accent, 0.45),
                            boxShadow: theme.shadows[2],
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: `${paletteKey}.main`,
                              fontWeight: 600,
                              fontSize: '1.5rem',
                              lineHeight: 1.25,
                              fontFamily: theme.typography.fontFamily,
                            }}
                          >
                            {value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                            {label}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })}
              </Grid>
            ) : null}

            {/* Jobs Table */}
            <TableContainer sx={tableContainerSx}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Run</TableCell>
                    <TableCell>Next Run</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(job.status)}
                          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem' }}>
                            {job.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {job.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {job.schedule}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={job.status}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {formatDate(job.lastRun)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {formatDate(job.nextRun)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => showJobDetailsDialog(job)}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: 'primary.main',
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  backgroundImage: 'none',
                                },
                              }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={job.enabled ? 'Disable' : 'Enable'}>
                            {actionLoading === job.id ? (
                              <span style={{ display: 'inline-flex' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleJob(job.id, !job.enabled)}
                                  disabled={actionLoading === job.id}
                                  sx={{
                                    color: job.enabled ? 'error.main' : 'success.main',
                                    '&:hover': {
                                      bgcolor: job.enabled
                                        ? alpha(theme.palette.error.main, 0.08)
                                        : alpha(theme.palette.success.main, 0.08),
                                      backgroundImage: 'none',
                                    },
                                  }}
                                >
                                  {actionLoading === job.id ? (
                                    <CircularProgress size={16} />
                                  ) : job.enabled ? (
                                    <PauseIcon fontSize="small" />
                                  ) : (
                                    <PlayIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => toggleJob(job.id, !job.enabled)}
                                disabled={actionLoading === job.id}
                                sx={{
                                  color: job.enabled ? 'error.main' : 'success.main',
                                  '&:hover': {
                                    bgcolor: job.enabled
                                      ? alpha(theme.palette.error.main, 0.08)
                                      : alpha(theme.palette.success.main, 0.08),
                                    backgroundImage: 'none',
                                  },
                                }}
                              >
                                {actionLoading === job.id ? (
                                  <CircularProgress size={16} />
                                ) : job.enabled ? (
                                  <PauseIcon fontSize="small" />
                                ) : (
                                  <PlayIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </Tooltip>
                          <Tooltip title="Run Now">
                            {actionLoading === job.id ? (
                              <span style={{ display: 'inline-flex' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => runJobNow(job.id)}
                                  disabled={actionLoading === job.id}
                                  sx={{
                                    color: 'info.main',
                                    '&:hover': {
                                      color: 'primary.main',
                                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                                      backgroundImage: 'none',
                                    },
                                  }}
                                >
                                  {actionLoading === job.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <PlayIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={() => runJobNow(job.id)}
                                disabled={actionLoading === job.id}
                                sx={{
                                  color: 'info.main',
                                  '&:hover': {
                                    color: 'primary.main',
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    backgroundImage: 'none',
                                  },
                                }}
                              >
                                {actionLoading === job.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <PlayIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>

      {/* Job Details Dialog */}
      <Dialog
        open={showJobDetails}
        onClose={() => setShowJobDetails(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: SERVER_JOBS_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={SERVER_JOBS_DIALOG_TITLE_SX}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.common.white, 0.2),
              backgroundImage: 'none',
              color: 'inherit',
              borderRadius: '8px',
              width: 40,
              height: 40,
              border: '1px solid',
              borderColor: alpha(theme.palette.common.white, 0.35),
            }}
          >
            <ScheduleIcon sx={{ fontSize: '1.25rem' }} />
          </Box>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'inherit' }}>
            {selectedJob?.name} - Execution History
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedJob ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem', lineHeight: 1.5 }}>
                {selectedJob.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <StatusChip status={selectedJob.status} />
                <Chip
                  label={`Schedule: ${selectedJob.schedule}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                    backgroundImage: 'none',
                  }}
                />
                <Chip
                  label={selectedJob.enabled ? 'Enabled' : 'Disabled'}
                  size="small"
                  variant="outlined"
                  color={selectedJob.enabled ? 'success' : 'default'}
                  sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                />
              </Box>
            </Box>
          ) : null}

          <TableContainer
            sx={{
              bgcolor: 'background.paper',
              backgroundImage: 'none',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      bgcolor: 'action.hover',
                      backgroundImage: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'text.primary',
                    }}
                  >
                    Start Time
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'action.hover',
                      backgroundImage: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'text.primary',
                    }}
                  >
                    Duration
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'action.hover',
                      backgroundImage: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'text.primary',
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: 'action.hover',
                      backgroundImage: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'text.primary',
                    }}
                  >
                    Result
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executions.map((execution) => (
                  <TableRow key={execution.id}>
                    <TableCell sx={{ color: 'text.primary', fontSize: '0.875rem' }}>
                      {formatDate(execution.startTime)}
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary', fontSize: '0.875rem' }}>
                      {execution.duration ? formatDuration(execution.duration) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={execution.status} />
                    </TableCell>
                    <TableCell>
                      {execution.error ? (
                        <Typography variant="body2" color="error" sx={{ fontSize: '0.875rem' }}>
                          {execution.error}
                        </Typography>
                      ) : execution.result ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {typeof execution.result === 'string' ? execution.result : JSON.stringify(execution.result)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {execution.status === 'completed' ? 'Success' : 'N/A'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={SERVER_JOBS_DIALOG_ACTIONS_SX}>
          <Button
            onClick={() => setShowJobDetails(false)}
            variant="outlined"
            color="inherit"
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              fontWeight: 500,
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                backgroundImage: 'none',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Job Dialog */}
      <Dialog
        open={showCreateJob}
        onClose={() => setShowCreateJob(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: SERVER_JOBS_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={SERVER_JOBS_DIALOG_TITLE_SX}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.common.white, 0.2),
              backgroundImage: 'none',
              color: 'inherit',
              borderRadius: '8px',
              width: 40,
              height: 40,
              border: '1px solid',
              borderColor: alpha(theme.palette.common.white, 0.35),
            }}
          >
            <SettingsIcon sx={{ fontSize: '1.25rem' }} />
          </Box>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'inherit' }}>
            Create New Job
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Job Name"
            fullWidth
            variant="outlined"
            value={newJob.name}
            onChange={(e) => setNewJob({ ...newJob, name: e.target.value })}
            sx={createJobFieldSx}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newJob.description}
            onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
            sx={createJobFieldSx}
          />
          <FormControl fullWidth margin="dense" sx={createJobFieldSx}>
            <InputLabel>Job template</InputLabel>
            <Select
              value={newJob.jobType}
              label="Job template"
              onChange={(e) =>
                setNewJob({ ...newJob, jobType: e.target.value as AdminJobType })
              }
            >
              {ADMIN_JOB_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {JOB_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" sx={createJobFieldSx}>
            <InputLabel>Schedule</InputLabel>
            <Select
              value={newJob.schedule}
              label="Schedule"
              onChange={(e) => setNewJob({ ...newJob, schedule: e.target.value })}
            >
              <MenuItem value="5m">Every 5 minutes</MenuItem>
              <MenuItem value="15m">Every 15 minutes</MenuItem>
              <MenuItem value="30m">Every 30 minutes</MenuItem>
              <MenuItem value="1h">Every hour</MenuItem>
              <MenuItem value="6h">Every 6 hours</MenuItem>
              <MenuItem value="12h">Every 12 hours</MenuItem>
              <MenuItem value="24h">Every 24 hours</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={SERVER_JOBS_DIALOG_ACTIONS_SX}>
          <Button
            onClick={() => setShowCreateJob(false)}
            variant="outlined"
            color="inherit"
            disabled={actionLoading === 'create'}
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              fontWeight: 500,
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              borderRadius: '8px',
              textTransform: 'none',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                backgroundImage: 'none',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={createJob}
            variant="contained"
            color="primary"
            disabled={actionLoading === 'create' || !newJob.name || !newJob.description}
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              borderRadius: '8px',
              textTransform: 'none',
            }}
            startIcon={actionLoading === 'create' ? <CircularProgress size={16} color="inherit" /> : <SettingsIcon />}
          >
            {actionLoading === 'create' ? 'Creating...' : 'Create Job'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default ServerJobsPanel
